/**
 * RecordLinkTargetPickerModal — picker celu powiązania (F.RL, powiązania między
 * kartotekami). Otwierany z `RecordLinksSection` po wybraniu typu powiązania —
 * `allowedTargetTypes` (whitelist z `RecordLinkTypeDTO.targetTypes`, pusta lista
 * u wołającego już rozwinięta do wszystkich typów) decyduje, które zakładki
 * typu encji są dostępne.
 *
 * **Architektura (po podmianie na wspólny picker tabelowy):** to CIENKI wrapper —
 * własne okno modalne (`<Modal>`) z paskiem zakładek rodzaju celu u góry, a pod nimi
 * współdzielony `RecordTablePicker` w trybie `embedded` (bez własnego okna — inaczej
 * mielibyśmy modal-in-modal). Każda zakładka montuje `RecordTablePicker` z `key={typ}`,
 * co przy przełączeniu wymusza czysty remount (świeża strona/filtry/klasa). Cały ciężar
 * (kolumny z katalogu pól, filtry, paginacja, selektor 7 klas dokumentów) niesie
 * `RecordTablePicker` — tu nie duplikujemy logiki list ani klas dokumentów.
 *
 * Single-pick — łączenie tworzy jedno powiązanie na raz. Wybór następuje kliknięciem
 * wiersza w tabeli (`RecordTablePicker` single-mode): woła `onChange(id, row)` → mapujemy
 * na `onPick({type, id, label})` i zamykamy okno. Etykieta z `adapter.getLabel(row)` jest
 * PRZEJŚCIOWA (chip „wybrano: X" w formularzu dodawania) — trwałą etykietę powiązania
 * nadaje backend przy zapisie (`sourceLabel`/`targetLabel`), klient jej nie wysyła.
 *
 * `companyId` — przekazany przez `RecordLinksSection` (aktywna firma; blok blokuje
 * „Dodaj powiązanie" gdy `null`, więc picker praktycznie zawsze dostaje realną wartość).
 * Dokładany jako `?companyId=` do zapytań list przez `RecordTablePicker`.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RecordLinkOwnerType } from '@saas/shared';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { RecordTablePicker } from '@/components/ui/RecordTablePicker';
import { getSystemEntityAdapter } from '@/lib/system-entity-adapters';

const TEST_ID = 'record-link-target-picker-modal';

const ENTITY_TYPES_ORDER: RecordLinkOwnerType[] = [
  'contractor',
  'employee',
  'product',
  'document',
  'task',
];

/**
 * Typ powiązania (`RecordLinkOwnerType`) → slug encji/adaptera `RecordTablePicker`
 * (`SYSTEM_ENTITY_ADAPTERS`). `document` → generyczny `documents` (adapter z
 * `classDimension` sam renderuje selektor 7 klas wewnątrz pickera).
 */
const OWNER_TYPE_TO_ENTITY: Record<RecordLinkOwnerType, string> = {
  contractor: 'contractors',
  employee: 'employees',
  product: 'products',
  document: 'documents',
  task: 'tasks',
};

export interface RecordLinkPickTarget {
  type: RecordLinkOwnerType;
  id: string;
  label: string;
}

export interface RecordLinkTargetPickerModalProps {
  open: boolean;
  /** Whitelist typów celu — wołający już rozwija pustą listę `targetTypes` do wszystkich typów. */
  allowedTargetTypes: RecordLinkOwnerType[];
  companyId: string | null;
  onClose: () => void;
  onPick: (target: RecordLinkPickTarget) => void;
}

export function RecordLinkTargetPickerModal({
  open,
  allowedTargetTypes,
  companyId,
  onClose,
  onPick,
}: RecordLinkTargetPickerModalProps) {
  const { t } = useTranslation();

  const availableTypes = ENTITY_TYPES_ORDER.filter((type) => allowedTargetTypes.includes(type));

  const [activeType, setActiveType] = useState<RecordLinkOwnerType | null>(null);

  // Reset aktywnej zakładki przy każdym otwarciu — pierwszy dostępny typ. `open` w
  // zależnościach: kolejne otwarcie startuje „na czysto", niezależnie od poprzedniej sesji.
  useEffect(() => {
    if (open) setActiveType(availableTypes[0] ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const entitySlug = activeType ? OWNER_TYPE_TO_ENTITY[activeType] : null;

  function handlePick(id: string, row: Record<string, unknown>) {
    if (!activeType || !entitySlug) return;
    const adapter = getSystemEntityAdapter(entitySlug);
    const label = adapter ? adapter.getLabel(row) : id;
    onPick({ type: activeType, id, label });
    // Zamknięcie zostawiamy `RecordTablePicker` — w single-mode sam woła `onClose` po
    // `onChange` (konwencja repo, patrz `vendorPicker`); NIE dublujemy go tutaj.
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('ui.recordLinkPicker.title')}
      size="xl"
      fixedHeight
    >
      <div data-testid={TEST_ID} className="flex flex-col gap-3">
        {availableTypes.length > 1 && (
          <div className="flex flex-wrap gap-1.5" data-testid={`${TEST_ID}-type-tabs`}>
            {availableTypes.map((type) => (
              <Button
                key={type}
                type="button"
                size="sm"
                variant={activeType === type ? 'primary' : 'ghost'}
                onClick={() => setActiveType(type)}
                data-testid={`${TEST_ID}-type-tab-${type}`}
              >
                {t(`ui.recordLinkPicker.entityTypes.${type}`)}
              </Button>
            ))}
          </div>
        )}

        {activeType && entitySlug ? (
          <RecordTablePicker
            key={activeType}
            embedded
            entity={entitySlug}
            open={open}
            companyId={companyId ?? undefined}
            onClose={onClose}
            onChange={handlePick}
            data-testid={`${TEST_ID}-table`}
          />
        ) : (
          <p
            className="py-8 text-center text-sm text-text-muted"
            data-testid={`${TEST_ID}-no-types`}
          >
            {t('ui.recordLinkPicker.emptyState')}
          </p>
        )}
      </div>
    </Modal>
  );
}
